<script lang="ts">
	//UI
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import DocumentStateSelector from "../component/Common/DocumentStateSelector.svelte";
	import EditorComposite from "../component/Notice/EditorComposite.svelte";
	import PlainTextEditor from "@hitit/hub/src/components/place/PlainTextEditor.svelte";
	import TinyMce from "@hitit/wiswig/src/MCE/TinyMCE.svelte";
	import { GoBackButton, Card, DefaultButton } from "@hitit/new_ui/src";
	//Utils
	import { style_formats, default_conf } from "@hitit/wiswig/src/MCE/constants";
	import { api, notifications, t } from "@hitit/hub/src/stores";
	import { onDestroy, onMount } from "svelte";
	import { navigate } from "svelte-routing";
	import {
		imageSetup,
		linkSetup,
		browserSizeMatch,
		forecolorSetup,
		completedUploadCallback,
	} from "@hitit/wiswig/src/MCE/utils";
	//Types
	import type { ActiveEditor } from "@hitit/wiswig/src/MCE/types";

	//Props
	export let noticeId = "";
	//Constants
	const stateType = ["OBSOLETE", "PUBLISHED", "DRAFT"];
	const initialWiswigValue = "<p></p>";
	const temporaryNewNoticeKey = `HITIT-temporary-new-notice`;
	const io = new IntersectionObserver(([e]) => {
		savebuttonSticky = !e.isIntersecting;
	});
	const throttleDelay = 300;
	const apiKey: string = "nqo1wyruv063uttoar1ywjr2hkey7nuca5ho7e4zne55govv";
	const keyboardOption = {
		bindings: {
			shift_enter: {
				key: 13,
				shiftKey: true,
				handler: () => {},
			},
			enter: {
				key: 13,
				handler: () => {},
			},
		},
	};

	//Variables
	let saving = false;
	let gettingNotice = false;
	let gettingEditor = true;
	let article = "";
	let title = ``;
	let state = "DRAFT";
	let timer: any = null;
	let isMobile = false;
	let savebuttonSticky = false;
	let savingInterval: any = null;
	let stickyDetector: HTMLDivElement;

	//
	$: loading = gettingEditor || gettingNotice || saving;
	$: conf = Object.assign({}, default_conf, {
		style_formats,
		setup,
		min_height: isMobile ? 600 : 700,
		toolbar_sticky: true,
		autoresize_bottom_margin: 0,
		autoresize_overflow_padding: 0,
		content_css: "/wiswig.css",
	});
	$: disableCondition =
		loading || title === "" || article === initialWiswigValue;
	//Functions
	const backClickHandler = () => history.back();
	const beforeunloadHandler = (e: BeforeUnloadEvent) => {
		// Only Chorme work
		e.preventDefault();
		e.returnValue = "";
		return "";
	};
	async function getNotice(noticeId: string) {
		try {
			gettingNotice = true;

			if (noticeId === "") return await setupNewNotice();
			// Get Data from Server
			const { data } = await $api.getNewsDocument(noticeId);

			//
			// Set Datas

			if (
				(await localNoticeHasAndSafe(noticeId)) &&
				confirm("임시 저장된 공지사항이 존재합니다. 불러 오시겠습니까 ?")
			) {
				const localNotice = await getTempNotice(
					noticeId ? noticeId : temporaryNewNoticeKey
				);

				article = localNotice.article;
				title = localNotice.title;
				state = localNotice.state;
			} else {
				article = data.newsDTO.content;
				title = data.newsDTO.title;
				state = data.newsDTO.documentState;
				//Set Data from API
			}

			// Local Saving
			if (savingInterval) clearInterval(savingInterval);

			savingInterval = setInterval(
				() => saveTempororyNewArticle(noticeId),
				1000
			);
		} catch (error) {
			console.log(error);
			notifications.append("ERROR", "공지사항 불러오기를 실패했습니다.");
			history.back();
		} finally {
			gettingNotice = false;
		}
	}

	async function saveNotice() {
		try {
			if (saving) return;
			if (savingInterval) clearInterval(savingInterval);
			saving = true;
			// Saving to sever And Get ID;
			const setupId = noticeId ? noticeId : undefined;
			const requestBody = {
				id: setupId,
				title: title,
				content: article,
				documentState: state as "OBSOLETE" | "PUBLISHED" | "DRAFT",
			};
			// variables
			let responseId = "";

			if (setupId === undefined)
				responseId = (await $api.postNewsDocument(requestBody)).data.newsDTO
					.id as string;
			else
				responseId = (await $api.updateNewsDocs(setupId, requestBody)).data
					.newsDTO.id as string;

			// Navigate New ID
			navigate(`/hubsetting/notice/read/${responseId}`);
			localStorage.removeItem(noticeId ? noticeId : temporaryNewNoticeKey);
		} catch (error) {
			console.log(error);
			saveTempororyNewArticle(noticeId ? noticeId : temporaryNewNoticeKey);
			notifications.append("ERROR", "저장중 문제가 발생하였습니다.");
		} finally {
			saving = false;
		}
	}

	async function getTempNotice(key: string) {
		type TempororySave = { article: string; title: string; state: string };
		const value = localStorage.getItem(key);

		const obj: TempororySave = await JSON.parse(value as any);

		return obj;
	}

	async function localNoticeHasAndSafe(key: string) {
		let success = true;
		try {
			// Temporory Props
			type TempororySave = { article: string; title: string; state: string };
			const props: (keyof TempororySave)[] = ["article", "title", "state"];
			const value = localStorage.getItem(key);
			const obj: TempororySave = await JSON.parse(value as any);

			for (const prop of props) {
				if (typeof obj[prop] !== "string") return false;
				if (
					prop === "state" &&
					stateType.find((v) => v === obj[prop]) === undefined
				)
					return false;
			}

			return true;
		} catch (error) {
			success = false;
		}
		return success;
	}

	function titleHandler(e: { detail: { text: string } }) {
		title = e.detail.text;
	}

	async function setupNewNotice() {
		if (
			(await localNoticeHasAndSafe(temporaryNewNoticeKey)) &&
			confirm("임시 저장된 공지사항이 존재합니다. 불러 오시겠습니까 ?")
		) {
			const localNotice = await getTempNotice(temporaryNewNoticeKey);
			article = localNotice.article;
			title = localNotice.title;
			state = localNotice.state;
		} else {
			article = initialWiswigValue;
			title = "";
			state = "DRAFT";
		}

		if (savingInterval) clearInterval(savingInterval);

		savingInterval = setInterval(
			() => saveTempororyNewArticle(temporaryNewNoticeKey),
			1000
		);
	}

	function setup(editor: ActiveEditor) {
		linkSetup(editor);
		browserSizeMatch(editor);
		forecolorSetup(editor);
		imageSetup(editor, completedUploadCallback);
		editor.on("init", () => {
			gettingEditor = false;
		});
	}

	function resizeEventHandler() {
		// For wiswig
		if (!timer) {
			timer = setTimeout(() => {
				timer = null;
				isMobile = window.innerWidth < 1024;
			}, throttleDelay);
		}
	}

	function saveTempororyNewArticle(key: string) {
		const tempNewArticle = {
			title,
			article,
			state,
		};

		if (title === "" && article === initialWiswigValue)
			return localStorage.removeItem(key);
		window.localStorage.setItem(key, JSON.stringify(tempNewArticle));
	}
	//Do
	$: !gettingEditor && getNotice(noticeId);

	window.addEventListener("resize", resizeEventHandler);
	window.addEventListener("beforeunload", beforeunloadHandler);
	onDestroy(() => {
		window.removeEventListener("resize", resizeEventHandler);
		window.removeEventListener("beforeunload", beforeunloadHandler);
		io.disconnect();
		if (savingInterval) clearInterval(savingInterval);
	});
	onMount(() => {
		io.observe(stickyDetector);
	});
</script>

{#if loading}
	<ContainerLoading />
{/if}
<div class="container">
	<div class="back">
		<GoBackButton on:click={backClickHandler} />
	</div>
	<Card noPadding>
		<div class="editor">
			<div class="state">
				<DocumentStateSelector bind:state={state} />
			</div>
			<div class="title">
				<EditorComposite title={$t("global.title")}>
					<div class="title-editor-wrapper">
						<PlainTextEditor
							on:UPDATE_TEXT={titleHandler}
							lengthLimit={Infinity}
							lineLimit={Infinity}
							keyboard={keyboardOption}
							value={title}
							reactiveId={gettingNotice ? Math.random().toString() : noticeId}
							reactiveValue
						/>
					</div>
				</EditorComposite>
			</div>
			<div class="article">
				<EditorComposite title={$t("global.article")}>
					<div class="dummy-editor" class:display={gettingEditor} />
					<TinyMce
						bind:value={article}
						apiKey={apiKey}
						conf={conf}
						consistentHeight={false}
					/>
				</EditorComposite>
				<div class="save-button" class:sticky={savebuttonSticky}>
					<DefaultButton
						fitmode
						on:click={saveNotice}
						disabled={disableCondition}
					>
						<span>{$t("global.save")}</span>
					</DefaultButton>
				</div>
				<div class="sticky-detector" bind:this={stickyDetector} />
			</div>
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../variables.scss";
	.container {
		width: 100%;
		display: flex;
		flex-direction: column;
		padding-top: 50px;
		position: relative;
		background-color: #f0f0f4;
		box-sizing: border-box;
		margin-top: -90px;

		.back {
			width: 30px;
			height: 27px;
			position: absolute;
			top: 0px;
			right: 0px;
		}

		.editor {
			display: flex;
			width: 100%;
			flex-direction: column;
			box-sizing: border-box;
			padding: 20px 10px;
			gap: 10px;

			.article {
				display: flex;
				flex-direction: column;
				gap: 20px;
				position: relative;
				.sticky-detector {
					position: absolute;
					bottom: 0;
				}

				.save-button {
					display: flex;
					justify-content: center;
					height: 32px;
					position: sticky;
					bottom: 20px;
					transition: all 0.5s;
					&.sticky {
						padding: 0px 20px;
					}

					span {
						font-size: 1rem;
						color: white;
						font-weight: 500;
						display: block;
						padding-bottom: 2px;
					}
				}
			}
			.dummy-editor {
				display: none;
				box-sizing: border-box;
				width: 100%;
				min-height: 600px;
				border: 2px solid #eee;
				border-radius: 10px;

				&.display {
					display: block;
				}
			}

			.title-editor-wrapper {
				width: 100%;
				height: auto;
			}
			:global(.ql-container) {
				height: auto;
			}
			:global(.ql-editor) {
				height: auto;
				min-height: 100px;
				background-color: #f0f0f4;
				box-sizing: border-box;
				overflow: auto;
				color: #46474b;
				font-weight: 200;
				padding: 10px;
				border-radius: 5px;
				line-height: 1.5625rem;
				&:focus {
					outline: 0px solid transparent;
				}

				&::-webkit-scrollbar {
					width: 2px;
				}
				&::-webkit-scrollbar-track {
					background-color: transparent;
				}

				&::-webkit-scrollbar-thumb {
					background-color: #bcbfc3;
				}
			}
			:global(.tox-tinymce--toolbar-sticky-on .tox-editor-header) {
				padding-top: 60px;
			}
			:global(.tox-editor-header) {
				transition: all 0.3s;
			}
		}
	}
	@include DesktopSize {
		.container {
			margin-top: 0px;

			.back {
				top: 0px;
			}
			.editor {
				padding: 30px;
				gap: 20px;
				.article {
					gap: 30px;
					.save-button {
						height: 40px;
						span {
							font-size: 1.25rem;
						}
					}
				}
				:global(.ql-editor) {
					font-size: 1.25rem;
					line-height: 2.1875rem;
				}
				:global(.tox-tinymce--toolbar-sticky-on .tox-editor-header) {
					padding-top: 4px;
				}
				.dummy-editor {
					height: 700px;
				}
			}
		}
	}
</style>
